import React, { useEffect, useState } from 'react';
import SubmitButton from '../../../components/Common/SubmitButton';
import { SchoolModel } from './services/School.service';
import { getActiveCountries, CountryOption } from '../../Teacher/services/Country.service';

interface SchoolFormProps {
  setShowForm: (value: boolean) => void;
  schoolDetails: Partial<SchoolModel> | null;
  onSave: (data: Partial<SchoolModel>) => Promise<void>;
}

const SchoolForm: React.FC<SchoolFormProps> = ({ setShowForm, schoolDetails, onSave }) => {
  const isEdit = !!schoolDetails?.id;
  const [loader, setLoader] = useState(false);
  const [countryOptions, setCountryOptions] = useState<CountryOption[]>([]);

  const [form, setForm] = useState<Partial<SchoolModel>>({
    id: schoolDetails?.id || undefined,
    name: schoolDetails?.name || '',
    externalId: schoolDetails?.externalId || '',
    country: schoolDetails?.country || 'US',
    state: schoolDetails?.state || '',
    city: schoolDetails?.city || '',
    zip: schoolDetails?.zip || '',
    address: schoolDetails?.address || '',
    phone: schoolDetails?.phone || '',
    website: schoolDetails?.website || '',
    schoolType: schoolDetails?.schoolType || '',
    level: schoolDetails?.level || '',
    districtName: schoolDetails?.districtName || '',
    enrollment: schoolDetails?.enrollment || undefined,
  });

  useEffect(() => {
    getActiveCountries().then(setCountryOptions);
  }, []);

  const isUSA = form.country === 'US';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'number') {
      setForm((prev) => ({ ...prev, [name]: value ? parseInt(value, 10) : undefined }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoader(true);
    try {
      await onSave(form);
    } finally {
      setLoader(false);
    }
  };

  const isValid = form.name && form.country;

  return (
    <div className="bg-white rounded-lg p-6 max-w-2xl">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">
          {isEdit ? 'Edit' : 'Create'} School
        </h2>
        <button
          onClick={() => setShowForm(false)}
          className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
        >
          &times;
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Name */}
          <div className="md:col-span-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              School Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="e.g. Lincoln Elementary School"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
            />
          </div>

          {/* State */}
          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
              {isUSA ? 'State (abbreviation)' : 'State / Province / Region'}
            </label>
            <input
              type="text"
              id="state"
              name="state"
              value={form.state}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  state: isUSA ? e.target.value.toUpperCase() : e.target.value,
                }))
              }
              placeholder={isUSA ? 'e.g. CA' : 'Optional'}
              maxLength={isUSA ? 2 : undefined}
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Country */}
          <div>
            <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
              Country *
            </label>
            <select
              id="country"
              name="country"
              value={form.country}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
              required
            >
              <option value="">Select country</option>
              {countryOptions.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* City */}
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={form.city}
              onChange={handleChange}
              placeholder="e.g. Sacramento"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Zip */}
          <div>
            <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1">
              Zip Code
            </label>
            <input
              type="text"
              id="zip"
              name="zip"
              value={form.zip}
              onChange={handleChange}
              placeholder="e.g. 95814"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Address */}
          <div className="md:col-span-2">
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="e.g. 123 School St"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="text"
              id="phone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="e.g. (916) 555-0100"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Website */}
          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              type="text"
              id="website"
              name="website"
              value={form.website}
              onChange={handleChange}
              placeholder="e.g. https://www.school.edu"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* External ID */}
          <div>
            <label htmlFor="externalId" className="block text-sm font-medium text-gray-700 mb-1">
              NCES School ID
            </label>
            <input
              type="text"
              id="externalId"
              name="externalId"
              value={form.externalId}
              onChange={handleChange}
              placeholder="e.g. 062271000123"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* School Type */}
          <div>
            <label htmlFor="schoolType" className="block text-sm font-medium text-gray-700 mb-1">
              School Type
            </label>
            <select
              id="schoolType"
              name="schoolType"
              value={form.schoolType}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">-- Select --</option>
              <option value="Regular">Regular</option>
              <option value="Special Education">Special Education</option>
              <option value="Vocational">Vocational</option>
              <option value="Alternative">Alternative</option>
              <option value="Charter">Charter</option>
              <option value="Magnet">Magnet</option>
              <option value="Private">Private</option>
            </select>
          </div>

          {/* Level */}
          <div>
            <label htmlFor="level" className="block text-sm font-medium text-gray-700 mb-1">
              Level
            </label>
            <select
              id="level"
              name="level"
              value={form.level}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">-- Select --</option>
              <option value="Elementary">Elementary</option>
              <option value="Middle">Middle</option>
              <option value="High">High</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* District Name */}
          <div>
            <label htmlFor="districtName" className="block text-sm font-medium text-gray-700 mb-1">
              District Name
            </label>
            <input
              type="text"
              id="districtName"
              name="districtName"
              value={form.districtName}
              onChange={handleChange}
              placeholder="e.g. Sacramento City Unified"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Enrollment */}
          <div>
            <label htmlFor="enrollment" className="block text-sm font-medium text-gray-700 mb-1">
              Enrollment
            </label>
            <input
              type="number"
              id="enrollment"
              name="enrollment"
              value={form.enrollment ?? ''}
              onChange={handleChange}
              placeholder="e.g. 500"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-2">
          <button
            type="button"
            onClick={() => setShowForm(false)}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <SubmitButton title={isEdit ? 'Update' : 'Create'} loader={loader} disable={!isValid} />
        </div>
      </form>
    </div>
  );
};

export default SchoolForm;
